import { useCallback, useMemo } from 'react'

import { format } from 'date-fns'

import { ProjectDocument } from '../../../api/resources/project'
import { Accordion } from '../../common/Accordion/Accordion'

interface ReportsCategoriesProps {
  documentsList?: ProjectDocument[]
  restrictCategory?: string | null
  onSelectDocument?: (document: ProjectDocument) => void
}

export const ReportsGrouping = ({
  documentsList = [],
  restrictCategory,
  onSelectDocument,
}: ReportsCategoriesProps) => {
  const documentsByMonthYear = useMemo(() => {
    const monthYearMap: Record<string, ProjectDocument[]> = {}

    documentsList.forEach(document => {
      if (
        document.date &&
        (!restrictCategory || document.category === restrictCategory)
      ) {
        const monthYear = format(new Date(document.date), 'MMMM yyyy')
        if (!monthYearMap[monthYear]) {
          monthYearMap[monthYear] = []
        }
        monthYearMap[monthYear].push(document)
      }
    })

    // Sort documents within each group by date (descending) and name
    Object.values(monthYearMap).forEach(docs => {
      docs.sort((a, b) => {
        const dateComparison =
          new Date(b.date).getTime() - new Date(a.date).getTime()
        if (dateComparison !== 0) return dateComparison
        return a.name.localeCompare(b.name)
      })
    })

    return monthYearMap
  }, [documentsList, restrictCategory])

  const handleSelectDocument = useCallback(
    (document: ProjectDocument) => {
      if (onSelectDocument) {
        onSelectDocument(document)
      }
    },
    [onSelectDocument],
  )

  const selectHandlers = useMemo(() => {
    const handlers: Record<number, () => void> = {}
    documentsList.forEach(document => {
      handlers[document.id] = () => handleSelectDocument(document)
    })
    return handlers
  }, [documentsList, handleSelectDocument])

  return (
    <Accordion>
      {documentsByMonthYear &&
        Object.entries(documentsByMonthYear).map(([monthYear, docs]) => (
          <Accordion.Item key={monthYear} title={monthYear}>
            {docs.map(document => (
              <span
                key={document.id}
                style={{ cursor: 'pointer' }}
                onClick={selectHandlers[document.id]}
              >
                {`${document.name} - ${format(new Date(document.date), 'MMM dd')}`}
              </span>
            ))}
          </Accordion.Item>
        ))}
    </Accordion>
  )
}
